import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import GpoFilter, { matchesGpo, FilterCount } from './GpoFilter';
import { printDocument, printBtnStyle } from '../utils/print';
import { ListFooter } from '../ui';

// Full Kitting (Order Execution Phase 2). Confirms every component of an order
// is received before PPM. Auto-pulls the BOM (FRS/ARS/GPO) and auto-fills
// received quantities from GRNs; the Production Incharge ticks each line and
// confirms complete, which opens the PPM hard gate (kittingGate, Phase 3).

const NAVY = '#2D2C72', INK = '#3B3A8F', RED = '#E22D2D', WARM = '#F7F6F3', VIOLET = '#EEEDF8';
const CONFIRMER_ROLES = ['production_incharge', 'admin', 'store'];
const GROUPS = [
  { key: 'fabric', label: '🧵 Fabric' },
  { key: 'trims', label: '🪡 Trims' },
  { key: 'accessories_packing', label: '📦 Accessories & Packing' },
];
const LINE_META = {
  received: { label: 'Received', bg: '#DDF3E4', fg: '#0F7B3F' },
  partial: { label: 'Partial', bg: '#FAEEDA', fg: '#854F0B' },
  pending: { label: 'Pending', bg: '#FBE2E2', fg: RED },
  // Not a quantity state. More than one PO matches this component by description, so no
  // receipt can be credited to it — the figures beside it were never computed. Purple to
  // read as "needs a decision", not as another shade of short.
  ambiguous: { label: 'Cannot verify', bg: '#EDE9FE', fg: '#5B21B6' },
};
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const qty = (v, u) => `${num(v).toLocaleString('en-IN', { maximumFractionDigits: 3 })}${u ? ' ' + u : ''}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const inputStyle = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const cardStyle = { background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: 18, marginBottom: 16 };
const sectionTitleStyle = { fontSize: 13, fontWeight: 700, color: INK, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px', paddingBottom: 6, borderBottom: `2px solid ${VIOLET}` };

function KpiCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: '14px 16px', flex: '1 1 120px' }}>
      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function FullKitting({ user, gpos: propGpos }) {
  const isConfirmer = CONFIRMER_ROLES.includes(user?.role);
  const [gpos, setGpos] = useState(propGpos || []);
  const [list, setList] = useState([]);
  const [gpoQ, setGpoQ] = useState('');   // GET /kitting is UNCAPPED — client-side is safe
  const [open, setOpen] = useState(null);          // { kitting, items, snapshot }
  const [pick, setPick] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(() => {
    api.get('/kitting').then((r) => setList(r.data.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => {
    if (propGpos && propGpos.length) { setGpos(propGpos); return; }
    api.get('/oe/gpos').then((r) => setGpos(r.data.data || [])).catch(() => {});
  }, [propGpos]);

  // Open (or create + auto-pull/auto-fill) the kitting for a GPO.
  const openFor = async (gpoNo) => {
    setSaving(true);
    try {
      const r = await api.post('/kitting', { gpo_no: gpoNo });
      setOpen(r.data.data);
      fetchList();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const refresh = async () => {
    if (!open?.kitting) return;
    setSaving(true);
    try {
      const r = await api.post(`/kitting/${open.kitting.id}/refresh`, {});
      setOpen((o) => ({ ...o, items: r.data.data.items }));
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const toggleConfirm = async (it) => {
    try {
      const r = await api.post(`/kitting/items/${it.id}/confirm`, { confirmed: !it.confirmed });
      setOpen((o) => ({ ...o, items: o.items.map((x) => x.id === it.id ? r.data.data : x) }));
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const confirmComplete = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/kitting/${open.kitting.id}/confirm-complete`, {});
      setOpen((o) => ({ ...o, kitting: r.data.data.kitting, items: r.data.data.items }));
      fetchList();
      alert('Full Kitting confirmed complete — the PPM gate is now open for this order.');
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const printKit = () => {
    const k = open.kitting, s = open.snapshot || {};
    const tbl = (g) => {
      const rows = open.items.filter((it) => it.grp === g.key);
      if (!rows.length) return null;
      return { title: g.label.replace(/^[^ ]+ /, ''), table: {
        columns: ['Component', 'Required', 'Received', 'Awaiting QC', 'Shortfall', 'On Order', 'Unordered', 'GRN Ref', 'Status', 'Confirmed'],
        rows: rows.map((it) => [
          it.component_name, qty(it.required_qty, it.unit), qty(it.received_qty, it.unit),
          qty(it.awaiting_qc, it.unit),
          qty(it.shortfall, it.unit), qty(it.on_order, it.unit), qty(it.unordered, it.unit), it.grn_ref || '—',
          (LINE_META[it.line_status] || {}).label || it.line_status, it.confirmed ? 'Yes' : 'No',
        ]),
      } };
    };
    printDocument({
      docTitle: 'Order Execution — Full Kitting',
      subtitle: `${k.kit_number || ''} · ${s.estimate_no || ''} · ${s.style || ''}`,
      meta: [
        ['Status', k.status === 'complete' ? 'COMPLETE' : 'Incomplete'],
        ['GPO', k.gpo_no || '—'],
        ['Confirmed by', k.confirmed_by_name || '—'],
        ['Confirmed on', fmtDateTime(k.confirmed_at)],
      ],
      sections: [
        { title: 'Order Snapshot', fields: [
          ['Buyer', s.buyer], ['Style', s.style], ['Description', s.garment_desc],
          ['Order Qty', s.order_qty], ['Planned Start', fmtDate(s.planned_start)],
        ] },
        ...GROUPS.map(tbl).filter(Boolean),
      ],
    });
  };

  // ─── LIST VIEW ────────────────────────────────────────────────────────────
  if (!open) {
    const withKit = new Set(list.map((r) => r.gpo_no));
    // Matches the GPO and the KIT number — the two keys people actually quote for a kit.
    const shown = list.filter((r) => matchesGpo(gpoQ, r.gpo_no, r.kit_number, r.estimate_no));
    const startable = gpos.filter((g) => !withKit.has(g.gpo_no));
    return (
      <div>
        <div style={{ ...cardStyle, background: VIOLET, border: 'none', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>📦 Open Full Kitting:</div>
          <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ ...inputStyle, minWidth: 280, background: '#fff' }}>
            <option value="">Select a GPO…</option>
            {gpos.map((g) => <option key={g.gpo_no} value={g.gpo_no}>GPO {g.gpo_no} — {g.style_no}{g.brand ? ' (' + g.brand + ')' : ''}{withKit.has(g.gpo_no) ? ' ✓' : ''}</option>)}
          </select>
          <button type="button" disabled={!pick || saving} onClick={() => { openFor(pick); setPick(''); }}
            style={{ padding: '8px 18px', background: pick ? INK : '#bbb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: pick ? 'pointer' : 'default' }}>
            {saving ? 'Opening…' : 'Open →'}
          </button>
          {!startable.length && gpos.length > 0 && <span style={{ fontSize: 12, color: '#888' }}>All GPOs already have Full Kitting — open one below.</span>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px' }}>
          <GpoFilter value={gpoQ} onChange={setGpoQ} placeholder="Filter by GPO / Kit no…" width={190}
            title='Partial match over GPO and Kit number — "175" finds 1750–1759' />
          <FilterCount shown={shown.length} total={list.length} noun="kits" q={gpoQ} />
        </div>
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
                {['Kit No', 'Order', 'Style / Buyer', 'Received', 'Status', 'Updated', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shown.length && <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>{list.length ? 'No kit matches that GPO / Kit no.' : 'No Full Kitting records yet — open one above.'}</td></tr>}
              {shown.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: NAVY }}>{r.kit_number || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>{r.estimate_no || '—'}{r.gpo_no ? <span style={{ color: '#999' }}> · GPO {r.gpo_no}</span> : ''}</td>
                  <td style={{ padding: '10px 14px' }}>{r.style_no || '—'}{r.brand ? <span style={{ color: '#999' }}> · {r.brand}</span> : ''}</td>
                  <td style={{ padding: '10px 14px', color: '#666' }}>{r.received_items}/{r.total_items}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {r.status === 'complete'
                      ? <span style={{ color: '#0F7B3F', fontWeight: 700 }}>✓ Complete</span>
                      : <span style={{ color: '#854F0B', fontWeight: 700 }}>● Incomplete</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#888', fontSize: 12 }}>{fmtDate(r.updated_at)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <button type="button" onClick={() => openFor(r.gpo_no)}
                      style={{ padding: '5px 14px', background: VIOLET, color: INK, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Open →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListFooter shown={list.length} total={list.length} noun="kitting requests" />
        </div>
      </div>
    );
  }

  // ─── DETAIL VIEW ──────────────────────────────────────────────────────────
  const k = open.kitting, s = open.snapshot || {}, items = open.items || [];
  const total = items.length;
  const received = items.filter((it) => it.line_status === 'received').length;
  const partial = items.filter((it) => it.line_status === 'partial').length;
  const pending = items.filter((it) => it.line_status === 'pending').length;
  const ambiguous = items.filter((it) => it.line_status === 'ambiguous');
  // A null reason reads as the description case — it is the older of the two, and what every
  // pre-288 row carried before the backfill named it.
  const ambByLine = ambiguous.filter((it) => it.ambiguous_reason === 'multi_line_po');
  const ambByDesc = ambiguous.filter((it) => it.ambiguous_reason !== 'multi_line_po');
  // An ambiguous line's shortfall was never computed, so `shortfall === 0` says nothing
  // about it. Without this the Complete button would stay enabled and the server would
  // reject the click — the block has to be visible BEFORE it is pressed.
  const allReceived = total > 0 && !ambiguous.length && items.every((it) => num(it.shortfall) === 0);
  const allTicked = total > 0 && items.every((it) => it.confirmed);
  const canComplete = allReceived && allTicked && k.status !== 'complete';
  const complete = k.status === 'complete';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <button type="button" onClick={() => { setOpen(null); fetchList(); }}
          style={{ padding: '6px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, color: NAVY, fontSize: 15 }}>{k.kit_number}</span>
          <button type="button" disabled={saving} onClick={refresh} style={{ ...printBtnStyle }}>🔄 Refresh from GRNs</button>
          <button type="button" onClick={printKit} style={printBtnStyle}>🖨️ Print / PDF</button>
        </div>
      </div>

      {/* Snapshot */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Order Snapshot <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#aaa', fontSize: 11 }}>— auto-filled</span></h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
          {[
            ['Buyer', s.buyer], ['Style', s.style], ['Description', s.garment_desc],
            ['Order Qty', s.order_qty != null ? Number(s.order_qty).toLocaleString('en-IN') + ' pcs' : '—'],
            ['GPO', k.gpo_no || '—'], ['Planned Start', fmtDate(s.planned_start)],
          ].map(([key, v]) => (
            <div key={key} style={{ background: WARM, borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>{key}</div>
              <div style={{ fontSize: 14, color: NAVY, fontWeight: 600, marginTop: 2 }}>{v || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <KpiCard label="Total Components" value={total} color={NAVY} />
        <KpiCard label="Received" value={received} color="#0F7B3F" />
        <KpiCard label="Partial" value={partial} color="#854F0B" />
        <KpiCard label="Pending" value={pending} color={RED} />
        {/* Only shown when it applies — a permanent zero here would read as a normal state. */}
        {ambiguous.length > 0 && <KpiCard label="Cannot Verify" value={ambiguous.length} color="#5B21B6" />}
      </div>

      {/* AMBIGUITY BANNER. The disabled Complete button alone does not say WHY, and this
          is the one block a user cannot resolve by chasing the vendor. */}
      {ambiguous.length > 0 && (
        <div style={{ background: '#EDE9FE', border: '1px solid #C4B5FD', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: '#5B21B6', fontSize: 13, marginBottom: 4 }}>
            Receipt cannot be verified for {ambiguous.length} component{ambiguous.length > 1 ? 's' : ''}
          </div>
          {/* TWO CAUSES that read the opposite way round (mig 288). The banner used to describe
              only the description collision, so a multi-line PO was explained as something it is
              not — and as something the reader could fix by picking the right PO, which they
              cannot. Each cause now gets its own sentence, and only the ones present are shown. */}
          <div style={{ fontSize: 12.5, color: '#4C1D95', lineHeight: 1.55 }}>
            {ambByDesc.length > 0 && (
              <div style={{ marginBottom: ambByLine.length ? 6 : 0 }}>
                <strong>{ambByDesc.length}</strong> match more than one purchase order by description, so no quantity can
                be credited without guessing which PO is which.
              </div>
            )}
            {ambByLine.length > 0 && (
              <div>
                <strong>{ambByLine.length}</strong> sit on a purchase order carrying several lines. The receipt is booked
                on the PO, not on the line, so there is no record of how much of it belongs to this component. The PO is
                not in doubt — the line is, and only a per-line receipt or a manual split resolves it.
              </div>
            )}
            <div style={{ marginTop: 6 }}>
              The received and shortfall figures for {ambiguous.length > 1 ? 'these lines' : 'this line'} are{' '}
              <strong>not computed</strong> — treat them as unknown, not as zero. Kitting cannot be completed until this
              is resolved.
            </div>
          </div>
          <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: 12.5, color: '#4C1D95' }}>
            {ambiguous.map((it) => (
              <li key={it.id} style={{ marginBottom: 2 }}>
                <strong>{it.component_name}</strong> — needs {qty(it.required_qty, it.unit)}; matched by:{' '}
                <span style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{it.grn_ref || '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Grouped component tables */}
      {GROUPS.map((g) => {
        const rows = items.filter((it) => it.grp === g.key);
        return (
          <div key={g.key} style={cardStyle}>
            <h3 style={sectionTitleStyle}>{g.label}</h3>
            {!rows.length ? (
              <div style={{ color: '#aaa', fontSize: 13, padding: '4px 0' }}>No {g.label.replace(/^[^ ]+ /, '').toLowerCase()} components found in the BOM.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
                    {['Component', 'Required', 'Received', 'Awaiting QC', 'Shortfall', 'On Order', 'Unordered', 'GRN Ref', 'Status', 'Confirm'].map((h) => (
                      <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((it) => {
                    const m = LINE_META[it.line_status] || LINE_META.pending;
                    // An ambiguous line's quantities were never computed — the stored numbers
                    // are whatever the last unambiguous sync left behind. Showing them would
                    // present a stale figure as a current one, which is the very confusion the
                    // flag exists to end. Show a dash and say so on hover.
                    const unknown = it.line_status === 'ambiguous';
                    const why = it.ambiguous_reason === 'multi_line_po'
                      ? 'Not computed — the receipt is booked on the PO, not on the line'
                      : 'Not computed — more than one PO matches this component';
                    const cell = (v) => unknown
                      ? <span style={{ color: '#5B21B6', fontWeight: 700 }} title={why}>—</span>
                      : qty(v, it.unit);
                    return (
                      <tr key={it.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#222' }}>{it.component_name}</td>
                        <td style={{ padding: '8px 12px' }}>{qty(it.required_qty, it.unit)}</td>
                        <td style={{ padding: '8px 12px' }}>{cell(it.received_qty)}</td>
                        {/* Received is QC-PASSED only (mig 202). This is what has arrived but
                            nobody has inspected — amber, because the action is chase QA, not
                            chase the vendor. It is NOT netted off the shortfall: the line IS
                            short until QC passes it. */}
                        <td style={{ padding: '8px 12px', color: num(it.awaiting_qc) > 0 ? '#B45309' : '#999', fontWeight: num(it.awaiting_qc) > 0 ? 700 : 400 }}
                          title={num(it.awaiting_qc) > 0 ? 'Received but not yet inspected — not counted as coverage' : ''}>{cell(it.awaiting_qc)}</td>
                        <td style={{ padding: '8px 12px', color: unknown ? '#5B21B6' : (num(it.shortfall) > 0 ? RED : '#0F7B3F'), fontWeight: 600 }}>{cell(it.shortfall)}</td>
                        {/* On Order = live PO quantity still to come. Unordered = the part of
                            the shortfall with no live PO behind it — the merchant raises a PO
                            for this, rather than chasing a vendor who was never asked. */}
                        <td style={{ padding: '8px 12px', color: '#555' }}>{qty(it.on_order, it.unit)}</td>
                        <td style={{ padding: '8px 12px', color: num(it.unordered) > 0 ? RED : '#999', fontWeight: num(it.unordered) > 0 ? 700 : 400 }}>{qty(it.unordered, it.unit)}</td>
                        <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>{it.grn_ref || '—'}</td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          <span style={{ background: m.bg, color: m.fg, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{m.label}</span>
                          {/* The quantity here was NOT read off a per-line receipt — it was
                              derived because one receipt covered the PO's whole order. That rests
                              on an assumption, so it is said rather than shown silently. */}
                          {it.split_derived && (
                            <span
                              title="Split derived from a full receipt — not recorded per line. One receipt covered this PO's entire order, so each line is credited its ordered quantity."
                              style={{ marginLeft: 6, background: '#E0F2FE', color: '#075985', padding: '3px 8px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, cursor: 'help' }}>
                              Split derived
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <input type="checkbox" checked={!!it.confirmed} disabled={!isConfirmer || complete}
                            onChange={() => toggleConfirm(it)}
                            title={isConfirmer ? 'Physical verification tick' : 'Only the Production Incharge can confirm'}
                            style={{ width: 18, height: 18, cursor: (isConfirmer && !complete) ? 'pointer' : 'not-allowed', accentColor: INK }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {/* Status bar + confirm */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        background: complete ? '#F2FBF5' : '#FFFCF5', borderColor: complete ? '#B8E6C8' : '#F3E2B8' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: complete ? '#0F7B3F' : '#854F0B' }}>
            {complete ? '✓ Kitting Complete' : '● Kitting Incomplete'}
          </div>
          <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
            {complete
              ? `Confirmed by ${k.confirmed_by_name || '—'} · ${fmtDateTime(k.confirmed_at)}`
              : `${allReceived ? 'All received' : 'Not all received'} · ${allTicked ? 'all confirmed' : 'not all confirmed'} — both required to complete.`}
          </div>
        </div>
        {!complete && (
          <button type="button" disabled={!canComplete || !isConfirmer || saving} onClick={confirmComplete}
            title={!isConfirmer ? 'Only the Production Incharge can confirm completion' : (!canComplete ? 'Every component must be fully received and ticked' : '')}
            style={{ padding: '11px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none',
              background: (canComplete && isConfirmer) ? '#0F7B3F' : '#c9c9d4',
              color: '#fff', cursor: (canComplete && isConfirmer) ? 'pointer' : 'not-allowed' }}>
            ✓ Confirm Kitting Complete
          </button>
        )}
      </div>

      {/* Hard-gate note */}
      <div style={{ background: VIOLET, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: INK }}>
        🔒 <b>Hard gate:</b> PPM (Phase 3) cannot be initiated for this order until Full Kitting is <b>Complete</b>.
        {complete ? ' This gate is now OPEN.' : ' Currently BLOCKED.'}
      </div>
    </div>
  );
}
